/**
 * Loventures Quiz (Production-ready)
 * - JSON-driven questions (radio, dropdown, textarea, text, email, multiselect)
 * - Radio can use layout: "chips" for wrap-style single-select (like multiselect look)
 * - Conditional questions: show_if (equals / includes)
 * - Saves answers/profile/pack to localStorage
 * - Attaches as line-item properties to your ONE product when it appears in cart
 * - Fullscreen, Hims-like centered layout, Loventures palette
 * - Uses the SAME logo from your navbar by cloning header logo DOM
 *
 * REQUIRED in theme:
 * <div id="lvQuizModalRoot"
 *   data-schema-url="{{ 'lv-quiz.schema.json' | asset_url }}"
 *   data-product-handle="YOUR_PRODUCT_HANDLE"
 *   data-property-prefix="Loventures">
 * </div>
 * Optional: data-logo-fallback-url="https://..." — used when the navbar logo isn't in the DOM (e.g. mobile).
 * Optional: data-cart-answers="hidden" | "summary" | "full" | "id" — how quiz appears in cart. "id" = only Quiz ID, City, and Constraints (3 line-item properties).
 * Optional: data-customer-create-url="https://..." — when set, quiz POSTs { email, name } when user
 *   reaches results so your backend can create a Shopify customer (Admin API). Quiz does not create customers itself.
 */

(() => {
  const root = document.getElementById("lvQuizModalRoot");
  if (!root) return;

  const schemaUrl = root.dataset.schemaUrl;
  const productHandle = root.dataset.productHandle;
  const propertyPrefix = root.dataset.propertyPrefix || "Loventures";

  if (!schemaUrl) {
    console.warn("[LVQ] Missing data-schema-url on #lvQuizModalRoot");
    return;
  }
  if (!productHandle) {
    console.warn("[LVQ] Missing data-product-handle on #lvQuizModalRoot");
    // We can still run the quiz UI, but cart attach won't work reliably.
  }

  // ---- Storage keys ----
  const LS = {
    seen: "lv_seen_v1",
    completed: "lv_quiz_completed_v1",
    answers: "lv_quiz_answers_v1",
    profile: "lv_quiz_profile_v1",
    pack: "lv_quiz_pack_v1",
    schemaId: "lv_quiz_schema_id_v1",
    history: "lv_quiz_history_v1",
    submissionId: "lv_quiz_submission_id_v1",
  };

  // Controls whether the modal should appear on first visit.
  const SHOW_ON_FIRST_VISIT = false;

  // DEV override (set true only while building; false for production)
  const DEV_ALWAYS_OPEN = false;
  const DEV_FORCE_OPEN_QUERY = new URLSearchParams(location.search).has("quiz"); // /?quiz=1

  // Optional: do not show modal on checkout pages
  const isCheckoutLike = /\/checkouts\//.test(location.pathname) || location.pathname === "/checkout";

  // Runtime state
  let schema = null;
  let answers = {};
  let currentQuestionId = null;
  let historyStack = [];
  /** Questions we've already shown this session — first time = no pre-selection; when returning via Back, show saved answer */
  let visitedQuestionIds = new Set();

  // Auto-attach loop
  let autoAttachTimer = null;

  // Variant IDs for our quiz products (so we can inject properties on add-to-cart)
  let quizVariantIds = new Set();

  // Focus management (a11y)
  let focusBeforeOpen = null;
  let focusTrapRef = null;
  let focusInRef = null;
  let enterKeyRef = null;

  init().catch(console.error);

  async function init() {
    if (isCheckoutLike) return;

    try {
      const res = await fetch(schemaUrl, { cache: "no-store" });
      if (!res.ok) throw new Error("Schema fetch failed");
      schema = await res.json();
    } catch (e) {
      console.error("[LVQ] Failed to load quiz schema", e);
      showSchemaError();
      return;
    }

    if (!schema || !Array.isArray(schema.questions) || schema.questions.length === 0) {
      console.error("[LVQ] Invalid or empty schema JSON");
      showSchemaError();
      return;
    }

    // Load saved state (if any)
    answers = loadSavedAnswers() || {};
    historyStack = loadSavedHistory() || [];

    const shouldOpen = DEV_ALWAYS_OPEN || DEV_FORCE_OPEN_QUERY || (!getBool(LS.seen) && SHOW_ON_FIRST_VISIT && !getBool(LS.completed));

    if (shouldOpen) {
      if (!DEV_ALWAYS_OPEN && !DEV_FORCE_OPEN_QUERY) setBool(LS.seen, true);
      openModal();
      setHeaderText();
      startQuizFromFirstQuestion();
      focusFirstInContent();
    } else {
      maybeAutoAttachLoop();
    }

    hookAjaxCart();
    populateQuizVariantIds();
  }

  /** Fetch quiz product handles and collect variant IDs so we can inject properties on add-to-cart. */
  function populateQuizVariantIds() {
    const handles = new Set();
    if (productHandle && productHandle.trim()) handles.add(productHandle.trim());
    const map = schema?.results?.product_by_profile;
    if (map && typeof map === "object") {
      Object.values(map).forEach(function (h) {
        if (h && String(h).trim()) handles.add(String(h).trim());
      });
    }
    handles.forEach(function (handle) {
      fetch("/products/" + encodeURIComponent(handle) + ".json", { cache: "no-store" })
        .then(function (r) {
          return r.json();
        })
        .then(function (data) {
          const variants = data?.product?.variants;
          if (Array.isArray(variants)) {
            variants.forEach(function (v) {
              if (v && v.id) quizVariantIds.add(v.id);
            });
          }
        })
        .catch(function () {});
    });
  }

  function showSchemaError() {
    if (!root) return;
    root.style.display = "block";
    root.innerHTML = `<p class="lvq-load-error">We couldn’t load the quiz. Please refresh the page or try again later.</p>`;
    injectStylesOnce();
  }

  // ----------------------------
  // Modal shell
  // ----------------------------
  function openModal() {
    focusBeforeOpen = document.activeElement && document.activeElement !== document.body ? document.activeElement : null;

    root.style.display = "block";
    root.innerHTML = modalShellHtml();
    document.body.classList.add("lvq-lock");

    injectStylesOnce();

    document.getElementById("lvqClose")?.addEventListener("click", closeModal);

    window.addEventListener("keydown", onKeydownEscape);
    focusTrapRef = handleFocusTrap;
    root.addEventListener("keydown", focusTrapRef);

    focusInRef = function (e) {
      if (!e.target || !e.target.matches("input, textarea, select")) return;
      setTimeout(function () {
        const actions = root.querySelector(".lvq-actions");
        if (actions) actions.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }, 350);
    };
    root.addEventListener("focusin", focusInRef);

    enterKeyRef = function (e) {
      if (e.key !== "Enter") return;
      const t = e.target;
      if (!t || !t.matches("input, textarea, select")) return;
      if (!root.contains(t)) return;
      if (!document.getElementById("lvqNext")) return;
      e.preventDefault();
      const q = schema.questions.find(function (x) {
        return x.id === currentQuestionId;
      });
      if (q) advanceFromCurrentQuestion(q);
    };
    root.addEventListener("keydown", enterKeyRef);
  }

  function closeModal() {
    root.style.display = "none";
    root.innerHTML = "";
    document.body.classList.remove("lvq-lock");

    window.removeEventListener("keydown", onKeydownEscape);
    if (focusTrapRef) {
      root.removeEventListener("keydown", focusTrapRef);
      focusTrapRef = null;
    }
    if (focusInRef) {
      root.removeEventListener("focusin", focusInRef);
      focusInRef = null;
    }
    if (enterKeyRef) {
      root.removeEventListener("keydown", enterKeyRef);
      enterKeyRef = null;
    }

    if (focusBeforeOpen && typeof focusBeforeOpen.focus === "function") {
      try {
        focusBeforeOpen.focus({ preventScroll: true });
      } catch (_) {}
    }
    focusBeforeOpen = null;

    maybeAutoAttachLoop();
  }

  function onKeydownEscape(e) {
    if (e.key === "Escape") closeModal();
  }

  function getModalFocusables() {
    const dialog = root.querySelector(".lvq-fullscreen");
    if (!dialog) return [];
    const sel =
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    return Array.from(dialog.querySelectorAll(sel)).filter((el) => el.offsetParent !== null && !el.hasAttribute("aria-hidden"));
  }

  function handleFocusTrap(e) {
    if (e.key !== "Tab") return;
    const focusables = getModalFocusables();
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  function focusFirstInContent() {
    requestAnimationFrame(() => {
      const container = document.getElementById("lvqContent");
      if (!container) return;
      const sel = "button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled])";
      const first = container.querySelector(sel);
      if (first && typeof first.focus === "function") first.focus({ preventScroll: false });
    });
  }

  function setHeaderText() {
    const sub = document.querySelector(".lvq-sub");
    if (sub) sub.textContent = schema.intro || "Answer a few quick questions and we’ll tailor your experience.";
  }

  function getNavbarLogoHTML() {
    // Try common Shopify header logo selectors (themes vary; mobile may use same or different structure)
    const candidates = [
      ".header__heading-link",
      ".header__heading",
      ".header__logo",
      ".site-header__logo",
      ".site-header__link",
      "header a[href='/']",
      "header a[href='" + location.origin + "/']",
    ];

    for (const sel of candidates) {
      const el = document.querySelector(sel);
      if (!el) continue;

      const img = el.querySelector("img, svg");
      if (img) {
        return `<div class="lvq-navbar-logo">${img.outerHTML}</div>`;
      }
      return `<div class="lvq-navbar-logo">${el.outerHTML}</div>`;
    }

    // Fallback: optional logo URL on root, or default Date Loventures logo (for mobile when header logo isn't in DOM)
    const DEFAULT_LOGO_FALLBACK =
      "https://dateloventures.com/cdn/shop/files/Logo_5.png?v=1735497702&width=160 1x, https://dateloventures.com/cdn/shop/files/Logo_5.png?v=1735497702&width=320 2x";
    let fallback =
      root.dataset.logoFallbackUrl || (typeof root.getAttribute === "function" ? root.getAttribute("data-logo-fallback-url") : null);
    if (!fallback || !fallback.trim()) fallback = DEFAULT_LOGO_FALLBACK;
    fallback = fallback.trim();
    // Parse srcset-style string ("url1 1x, url2 2x") into src (first URL) and optional srcset
    const parts = fallback.split(",").map((s) => s.trim());
    const firstPart = parts[0] || "";
    const src = firstPart
      .replace(/\s*1x\s*$/i, "")
      .trim()
      .replace(/%20/g, " ");
    const srcsetHtml = parts.length > 1 ? ` srcset="${escapeAttr(parts.map((p) => p.replace(/%20/g, " ")).join(", "))}"` : "";
    return `<div class="lvq-navbar-logo"><img src="${escapeAttr(
      src
    )}"${srcsetHtml} alt="Logo" class="lvq-fallback-logo" width="160" height="32" loading="eager" /></div>`;
  }

  /** Always returns the fallback logo img (never navbar clone). Use for in-modal header so logo always shows on mobile. */
  function getHeaderLogoHTML() {
    const DEFAULT_LOGO_FALLBACK =
      "https://dateloventures.com/cdn/shop/files/Logo_5.png?v=1735497702&width=160 1x, https://dateloventures.com/cdn/shop/files/Logo_5.png?v=1735497702&width=320 2x";
    let fallback =
      root.dataset.logoFallbackUrl || (typeof root.getAttribute === "function" ? root.getAttribute("data-logo-fallback-url") : null);
    if (!fallback || !fallback.trim()) fallback = DEFAULT_LOGO_FALLBACK;
    fallback = fallback.trim();
    const parts = fallback.split(",").map((s) => s.trim());
    const firstPart = parts[0] || "";
    const src = firstPart
      .replace(/\s*1x\s*$/i, "")
      .trim()
      .replace(/%20/g, " ");
    const srcsetHtml = parts.length > 1 ? ` srcset="${escapeAttr(parts.map((p) => p.replace(/%20/g, " ")).join(", "))}"` : "";
    return `<div class="lvq-navbar-logo"><img src="${escapeAttr(
      src
    )}"${srcsetHtml} alt="Logo" class="lvq-fallback-logo" width="160" height="32" loading="eager" /></div>`;
  }

  function modalShellHtml() {
    return `
        <div class="lvq-fullscreen" role="dialog" aria-modal="true" aria-label="Loventures quiz">
          <div class="lvq-topbar">
            <div class="lvq-brand">
              ${getHeaderLogoHTML()}
            </div>
            <button class="lvq-close" id="lvqClose" aria-label="Close">Close</button>
          </div>
  
          <div class="lvq-shell">
            <div class="lvq-column">
              <div class="lvq-header">
                <div class="lvq-header-logo">${getHeaderLogoHTML()}</div>
                <p class="lvq-sub"></p>
              </div>
  
              <div class="lvq-card">
                <div class="lvq-card__inner" id="lvqContent"></div>
              </div>
            </div>
          </div>
        </div>
      `;
  }

  function contentEl() {
    return document.getElementById("lvqContent");
  }

  // ----------------------------
  // Quiz rendering (no intro — first question immediately for better conversion)
  // ----------------------------
  function renderIntro() {
    const el = contentEl();
    if (!el) return;
    const introCopy = schema.intro || "Answer a few quick questions and we'll tailor your experience.";
    el.innerHTML = `
        <div class="lvq-card__scroll">
          <p class="lvq-p lvq-intro-text">${escapeHtml(introCopy)}</p>
        </div>
        <div class="lvq-actions">
          <button type="button" class="lvq-btn lvq-btn--primary" id="lvqBegin">Begin</button>
        </div>
      `;
    scrollContentToTop();
    document.getElementById("lvqBegin").onclick = () => {
      startQuizFromFirstQuestion();
      focusFirstInContent();
    };
  }

  function startQuizFromFirstQuestion() {
    answers = loadSavedAnswers() || {};
    historyStack = [];
    visitedQuestionIds = new Set();
    currentQuestionId = getFirstVisibleQuestionId();
    if (!currentQuestionId) {
      console.error("[LVQ] No visible question to show");
      closeModal();
      return;
    }
    saveProgress();
    renderQuestion();
  }

  function renderQuestion() {
    const q = schema.questions.find((x) => x.id === currentQuestionId);

    // currentQuestionId might be a results page id (e.g. results_full, results_optin) from next
    if (!q) {
      const pageId = schema.results?.pages?.[currentQuestionId] != null ? currentQuestionId : inferResultsPageId();
      return renderResultsPage(pageId);
    }

    // If we landed on a question that's hidden by show_if, skip to next (e.g. constraints -> constraints_other when "other" not selected)
    if (!isQuestionVisible(q)) {
      const skipNext = getNextQuestionId(q, answers[q.id]);
      if (skipNext) {
        currentQuestionId = skipNext;
        return renderQuestion();
      }
      return renderResultsPage(inferResultsPageId());
    }

    const visible = schema.questions.filter(isQuestionVisible);
    const idx = visible.findIndex((x) => x.id === currentQuestionId);

    const progress = Math.min(100, Math.round((idx + 1) * (100 / 10)));
    const progressCopy = getProgressCopy(progress);

    // First time showing this question: no option selected. When returning via Back, show saved answer.
    let value;
    if (!visitedQuestionIds.has(q.id)) {
      value = q.type === "multiselect" ? [] : "";
      visitedQuestionIds.add(q.id);
    } else {
      value = answers[q.id] ?? (q.type === "multiselect" ? [] : "");
    }

    const el = contentEl();
    if (!el) return;

    const titleId = "lvq-q-title-" + cssEscape(q.id);
    el.innerHTML = `
        <div class="lvq-card__scroll">
          <div class="lvq-progress-wrap">
            <div class="lvq-progress" role="progressbar" aria-valuenow="${progress}" aria-valuemin="0" aria-valuemax="100" aria-label="Quiz progress"><div class="lvq-progress__bar" style="width:${progress}%"></div></div>
            <p class="lvq-progress-copy" aria-live="polite">${escapeHtml(progressCopy)}</p>
          </div>
          <h2 class="lvq-h2" id="${escapeAttr(titleId)}">${escapeHtml(q.title || "")}</h2>
          <div class="lvq-field" role="group" aria-labelledby="${escapeAttr(titleId)}">
            ${renderInput(q, value, titleId)}
            ${q.required ? `<div class="lvq-hint">Required</div>` : ``}
          </div>
        </div>
        <div class="lvq-actions">
          <button type="button" class="lvq-btn lvq-btn--ghost" id="lvqBack" ${historyStack.length === 0 ? "disabled" : ""}>Back</button>
          <button type="button" class="lvq-btn lvq-btn--primary" id="lvqNext">Next</button>
        </div>
        <div class="lvq-error" id="lvqError" role="alert" aria-live="polite" style="display:none;"></div>
      `;

    scrollContentToTop();

    // Wire radio: update selection + auto-advance (works for both stacked and chip layout)
    if (q.type === "radio") {
      const wrap = el.querySelector(`.lvq-options-wrap[data-qid="${cssEscape(q.id)}"]`);
      const container = wrap?.querySelector(".lvq-options");
      const hidden = document.getElementById(`lvq_${q.id}`);
      container?.querySelectorAll(".lvq-option").forEach((btn) => {
        btn.addEventListener("click", () => {
          hidden.value = btn.dataset.value || "";
          container.querySelectorAll(".lvq-option").forEach((b) => {
            b.classList.remove("is-selected");
            b.setAttribute("aria-checked", "false");
          });
          btn.classList.add("is-selected");
          btn.setAttribute("aria-checked", "true");
          advanceFromCurrentQuestion(q);
        });
      });
    }

    // Wire multiselect chips
    if (q.type === "multiselect") {
      const wrap = el.querySelector(`.lvq-chips-wrap[data-qid="${cssEscape(q.id)}"]`);
      const container = wrap?.querySelector(".lvq-chips");
      const hidden = document.getElementById(`lvq_${q.id}`);
      container?.querySelectorAll(".lvq-chip").forEach((btn) => {
        btn.addEventListener("click", () => {
          let current = [];
          try {
            current = JSON.parse(hidden.value || "[]");
          } catch {}

          const v = btn.dataset.value;
          const idx = current.indexOf(v);

          // If they select "none", clear other selections
          if (v === "none") {
            current = ["none"];
            container.querySelectorAll(".lvq-chip").forEach((b) => b.classList.remove("is-selected"));
            btn.classList.add("is-selected");
          } else {
            // Remove "none" if selecting anything else
            current = current.filter((x) => x !== "none");
            if (idx === -1) current.push(v);
            else current.splice(current.indexOf(v), 1);
            btn.classList.toggle("is-selected");
            // Ensure "none" chip isn't selected
            const noneChip = container.querySelector(`.lvq-chip[data-value="none"]`);
            noneChip?.classList.remove("is-selected");
          }

          hidden.value = JSON.stringify(current);
        });
      });
    }

    document.getElementById("lvqBack").onclick = () => {
      const prev = historyStack.pop();
      if (!prev) return;
      hideError();
      currentQuestionId = prev;
      saveProgress();
      renderQuestion();
      scrollContentToTop();
      focusFirstInContent();
    };

    document.getElementById("lvqNext").onclick = () => advanceFromCurrentQuestion(q);
  }

  function advanceFromCurrentQuestion(q) {
    hideError();
    const nextVal = readInput(q);

    if (q.required && isEmpty(nextVal)) return showError("Please answer before continuing.");

    if (q.type === "email") {
      if (q.required && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(nextVal || ""))) return showError("Please enter a valid email.");
    }

    answers[q.id] = nextVal;
    if (q.type === "email") {
      const consentEl = document.getElementById(`lvq_${q.id}_consent`);
      answers[q.id + "_consent"] = consentEl && consentEl.checked;
    }
    purgeHiddenAnswers();

    const nextId = getNextQuestionId(q, nextVal);

    if (!nextId) {
      saveProgress();
      return renderResultsPage(inferResultsPageId());
    }

    historyStack.push(currentQuestionId);
    currentQuestionId = nextId;

    saveProgress();
    renderQuestion();
    scrollContentToTop();
    focusFirstInContent();
  }

  function getProgressCopy(progress) {
    if (progress <= 20) return "Let's go";
    if (progress <= 40) return "Great start";
    if (progress <= 60) return "Halfway there";
    if (progress <= 80) return "You're almost there";
    return "Almost done";
  }

  function scrollContentToTop() {
    const container = document.getElementById("lvqContent");
    if (container) container.scrollTop = 0;
    const scrollEl = container?.querySelector(".lvq-card__scroll");
    if (scrollEl) scrollEl.scrollTop = 0;
    const card = root.querySelector(".lvq-card");
    if (card) card.scrollTop = 0;
  }

  function getCityLabelForResults() {
    const cityVal = answers?.city;
    if (!cityVal || cityVal === "other") return null;
    const cityQ = schema.questions.find((q) => q.id === "city");
    if (!cityQ?.options) return null;
    const opt = cityQ.options.find((o) => o.value === cityVal);
    return opt ? opt.label : null;
  }

  /** City label from saved answers (for cart). */
  function getCityLabelFromAnswers(answersObj) {
    if (!answersObj || !schema?.questions) return "";
    const cityVal = answersObj.city;
    if (!cityVal || cityVal === "other") return "";
    const cityQ = schema.questions.find((q) => q.id === "city");
    if (!cityQ?.options) return "";
    const opt = cityQ.options.find((o) => o.value === cityVal);
    return opt ? opt.label : "";
  }

  /** Constraints text from saved answers (for cart). Main constraints + constraints_other if present. */
  function getConstraintsFromAnswers(answersObj) {
    if (!answersObj || typeof answersObj !== "object") return "";
    const main = (answersObj.constraints && String(answersObj.constraints).trim()) || "";
    const other = (answersObj.constraints_other && String(answersObj.constraints_other).trim()) || "";
    if (main && other) return main + " — " + other;
    return main || other;
  }

  /** Budget label from saved answers (for customer note). */
  function getBudgetLabelFromAnswers(answersObj) {
    if (!answersObj || !schema?.questions) return "";
    const budgetVal = answersObj.budget;
    if (!budgetVal) return "";
    const budgetQ = schema.questions.find((q) => q.id === "budget");
    if (!budgetQ?.options) return "";
    const opt = budgetQ.options.find((o) => o.value === budgetVal);
    return opt ? opt.label : "";
  }

  /** Human-readable "date night style" for results. Only the 5 canonical labels are used. */
  const FIVE_STYLE_LABELS = {
    adventurous_romantics: "Adventurous Romantics",
    cozy_intimates: "Cozy Intimates",
    curious_explorers: "Curious Explorers",
    social_sparks: "Social Sparks",
    playful_competitors: "Playful Competitors",
  };
  function getProfileStyleLabel(profileId) {
    if (!profileId) return FIVE_STYLE_LABELS.adventurous_romantics;
    const labels = schema.results?.profile_labels || FIVE_STYLE_LABELS;
    if (labels && typeof labels[profileId] === "string") return labels[profileId];
    if (FIVE_STYLE_LABELS[profileId]) return FIVE_STYLE_LABELS[profileId];
    return FIVE_STYLE_LABELS.adventurous_romantics;
  }

  /** Product handle for this profile (results_full CTA). Uses schema.results.product_by_profile[profile], else root data-product-handle. */
  function getProductHandleForProfile(profileId) {
    const map = schema.results?.product_by_profile;
    if (map && typeof map[profileId] === "string" && map[profileId].trim()) return map[profileId].trim();
    return productHandle || "";
  }

  function renderResultsPage(pageId) {
    // Compute / persist profile + pack always
    const result = computeResult();
    persistResult(result);

    const page = schema.results?.pages?.[pageId] || null;

    const el = contentEl();
    if (!el) return;

    const cityLabel = getCityLabelForResults();
    const isFullResults = pageId === "results_full";
    const styleLabel = getProfileStyleLabel(result.profile);

    const resultsHeadline = isFullResults ? null : page?.headline || "You're all set!";
    const fullResultsHeadlineHtml = isFullResults
      ? `<span class="lvq-results-headline__intro">Your Relationship Date night style is...</span><span class="lvq-results-headline__style">${escapeHtml(
          styleLabel
        )}</span>`
      : null;
    const resultsSub = isFullResults
      ? cityLabel
        ? "Your " + cityLabel + " date plan is ready. One tap to find out more."
        : "Your personalized date plan is ready. One tap to find out more."
      : page?.body || "We've got you.";
    const ctaLabel = isFullResults ? "See my dates" : page?.cta_primary?.label || "Continue";
    const footCopy = isFullResults
      ? "We'll remember your answers — add to cart anytime."
      : "If you add Loventures to cart later, we'll apply this automatically.";

    const productForProfile = isFullResults ? getProductHandleForProfile(result.profile) : productHandle || "";
    const ctaPrimaryHref = page?.cta_primary?.href
      ? page.cta_primary.href.replace("YOUR_PRODUCT_HANDLE", encodeURIComponent(productForProfile || productHandle || ""))
      : null;

    // Single-step results: headline + sub + CTA (for full results the "reveal" is going to the product page)
    const fullResultsHtml = isFullResults
      ? `
        <div class="lvq-results-success">
          <div class="lvq-results-success__icon" aria-hidden="true">
            <svg width="72" height="72" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M28 50C28 50 4 32 4 18C4 10 12 4 20 4C26 4 28 8 28 8C28 8 30 4 36 4C44 4 52 10 52 18C52 32 28 50 28 50Z" fill="var(--lvq-primary)" fill-opacity="0.95"/>
            </svg>
          </div>
          <h2 class="lvq-results-headline">${fullResultsHeadlineHtml}</h2>
          <p class="lvq-results-sub">${escapeHtml(resultsSub)}</p>
        </div>
        <div class="lvq-actions lvq-actions--stacked">
          ${
            ctaPrimaryHref
              ? `<a class="lvq-btn lvq-btn--primary" href="${escapeAttr(ctaPrimaryHref)}">${escapeHtml(ctaLabel)}</a>`
              : `<button class="lvq-btn lvq-btn--primary" id="lvqApply">${escapeHtml(ctaLabel)}</button>`
          }
        </div>
        <div class="lvq-results-secondary">
          ${page?.cta_secondary?.action === "edit" ? `<button type="button" class="lvq-link" id="lvqEdit">Edit answers</button>` : ""}
        </div>
        <p class="lvq-foot">${escapeHtml(footCopy)}</p>
      `
      : "";

    const standardResultsHtml = !isFullResults
      ? `
        <div class="lvq-results-success">
          <div class="lvq-results-success__icon" aria-hidden="true">
            <svg width="72" height="72" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M28 50C28 50 4 32 4 18C4 10 12 4 20 4C26 4 28 8 28 8C28 8 30 4 36 4C44 4 52 10 52 18C52 32 28 50 28 50Z" fill="var(--lvq-primary)" fill-opacity="0.95"/>
            </svg>
          </div>
          <h2 class="lvq-results-headline">${escapeHtml(resultsHeadline)}</h2>
          <p class="lvq-results-sub">${escapeHtml(resultsSub)}</p>
        </div>
        <div class="lvq-actions lvq-actions--stacked">
          ${
            ctaPrimaryHref
              ? `<a class="lvq-btn lvq-btn--primary" href="${escapeAttr(ctaPrimaryHref)}">${escapeHtml(ctaLabel)}</a>`
              : `<button class="lvq-btn lvq-btn--primary" id="lvqApply">${escapeHtml(ctaLabel)}</button>`
          }
        </div>
        <div class="lvq-results-secondary">
          ${page?.cta_secondary?.action === "edit" ? `<button type="button" class="lvq-link" id="lvqEdit">Edit answers</button>` : ""}
          ${
            page?.cta_secondary?.action === "restart"
              ? `<button type="button" class="lvq-link" id="lvqRestart">Take the quiz again</button>`
              : ""
          }
        </div>
        <p class="lvq-foot">${escapeHtml(footCopy)}</p>
      `
      : "";

    el.innerHTML = fullResultsHtml || standardResultsHtml;

    // Secondary actions
    const editBtn = document.getElementById("lvqEdit");
    if (editBtn) {
      editBtn.onclick = () => {
        const prev = historyStack.pop();
        currentQuestionId = prev || getFirstVisibleQuestionId();
        saveProgress();
        renderQuestion();
      };
    }

    const restartBtn = document.getElementById("lvqRestart");
    if (restartBtn) {
      restartBtn.onclick = () => {
        answers = {};
        historyStack = [];
        visitedQuestionIds = new Set();
        currentQuestionId = getFirstVisibleQuestionId();
        clearSavedProgressOnly();
        saveProgress();
        renderQuestion();
      };
    }

    const applyBtn = document.getElementById("lvqApply");
    if (applyBtn) {
      applyBtn.onclick = async () => {
        const label = applyBtn.textContent;
        applyBtn.disabled = true;
        applyBtn.textContent = "Applying…";
        try {
          await attachToCartIfPresent();
          closeModal();
        } catch (e) {
          console.error(e);
          applyBtn.disabled = false;
          applyBtn.textContent = label;
          alert("Couldn’t apply customization yet. If you add the product to cart, we’ll apply it automatically.");
          closeModal();
        }
      };
    }

    scrollContentToTop();
    focusFirstInContent();

    tryCreateCustomerFromAnswers();

    maybeAutoAttachLoop();
  }

  // ----------------------------
  // Input rendering
  // ----------------------------

  function renderInput(q, value, titleId) {
    const id = `lvq_${q.id}`;
    const a11yLabel = titleId ? ` aria-labelledby="${escapeAttr(titleId)}"` : "";

    if (q.type === "radio") {
      const groupLabel = (q.title || "").slice(0, 80);
      const isChipLayout = q.layout === "chips";
      const containerClass = isChipLayout ? "lvq-options lvq-options--chips" : "lvq-options";
      const optionClass = isChipLayout ? "lvq-option lvq-option--chip" : "lvq-option";
      const opts = q.options || [];
      return `
          <div class="lvq-options-wrap" data-qid="${escapeAttr(q.id)}">
            <div class="${escapeAttr(containerClass)}" role="radiogroup" aria-label="${escapeAttr(groupLabel)}">
              ${opts
                .map(
                  (opt) => `
              <button type="button"
                class="${escapeAttr(optionClass)} ${value === opt.value ? "is-selected" : ""}"
                data-value="${escapeAttr(opt.value)}"
                role="radio"
                aria-checked="${value === opt.value ? "true" : "false"}">
                ${escapeHtml(opt.label)}
              </button>
            `
                )
                .join("")}
            </div>
            <input type="hidden" id="${escapeAttr(id)}" value="${escapeAttr(value)}" />
          </div>
        `;
    }

    if (q.type === "dropdown") {
      return `
          <select id="${escapeAttr(id)}" class="lvq-select"${a11yLabel}>
            <option value="" ${value === "" ? "selected" : ""}>Select one</option>
            ${(q.options || [])
              .map(
                (opt) => `
              <option value="${escapeAttr(opt.value)}" ${value === opt.value ? "selected" : ""}>
                ${escapeHtml(opt.label)}
              </option>
            `
              )
              .join("")}
          </select>
        `;
    }

    if (q.type === "multiselect") {
      let arr = [];
      if (Array.isArray(value)) arr = value;
      else if (typeof value === "string" && value.trim().startsWith("[")) {
        try {
          arr = JSON.parse(value);
        } catch {
          arr = [];
        }
      } else if (value) arr = [value];

      const opts = q.options || [];
      return `
          <div class="lvq-chips-wrap" data-qid="${escapeAttr(q.id)}">
            <div class="lvq-chips">
              ${opts
                .map((opt) => {
                  const selected = arr.includes(opt.value);
                  return `
                <button type="button"
                  class="lvq-chip ${selected ? "is-selected" : ""}"
                  data-value="${escapeAttr(opt.value)}">
                  ${escapeHtml(opt.label)}
                </button>
              `;
                })
                .join("")}
            </div>
            <input type="hidden" id="${escapeAttr(id)}" value="${escapeAttr(JSON.stringify(arr))}" />
          </div>
        `;
    }

    if (q.type === "textarea") {
      return `<textarea id="${escapeAttr(id)}" class="lvq-textarea" placeholder="${escapeAttr(
        q.placeholder || ""
      )}"${a11yLabel}>${escapeHtml(value)}</textarea>`;
    }

    if (q.type === "email") {
      const consentLabel = (
        q.consent_label || "I agree to receive email updates, date ideas, and marketing from Loventures. I can unsubscribe anytime."
      ).trim();
      const consentId = id + "_consent";
      const consentChecked = answers[q.id + "_consent"] !== false;
      return `
        <input id="${escapeAttr(id)}" class="lvq-input" type="email" inputmode="email" autocomplete="email"
          value="${escapeAttr(typeof value === "string" ? value : "")}" placeholder="${escapeAttr(
        q.placeholder || "you@example.com"
      )}"${a11yLabel} />
        <div class="lvq-email-consent">
          <label class="lvq-consent-label">
            <input type="checkbox" id="${escapeAttr(consentId)}" class="lvq-consent-checkbox" ${consentChecked ? "checked" : ""} />
            <span class="lvq-consent-text">${escapeHtml(consentLabel)}</span>
          </label>
        </div>`;
    }

    return `<input id="${escapeAttr(id)}" class="lvq-input" type="text" value="${escapeAttr(value)}" placeholder="${escapeAttr(
      q.placeholder || ""
    )}"${a11yLabel} />`;
  }

  function readInput(q) {
    const id = `lvq_${q.id}`;

    if (q.type === "multiselect") {
      const hidden = document.getElementById(id);
      try {
        return JSON.parse(hidden?.value || "[]");
      } catch {
        return [];
      }
    }

    const el = document.getElementById(id);
    return el ? (el.value || "").trim() : "";
  }

  // ----------------------------
  // Branching + visibility
  // ----------------------------
  function getFirstVisibleQuestionId() {
    for (const q of schema.questions) {
      if (isQuestionVisible(q)) return q.id;
    }
    return null;
  }

  function getQuestionIndexById(qid) {
    return schema.questions.findIndex((q) => q.id === qid);
  }

  function isQuestionVisible(q) {
    if (!q.show_if) return true;

    const { question_id, equals, includes } = q.show_if;
    const val = answers?.[question_id];

    if (typeof equals !== "undefined") return String(val ?? "") === String(equals);
    if (typeof includes !== "undefined") return Array.isArray(val) && val.includes(includes);

    return true;
  }

  function getNextQuestionId(currentQ, selectedValue) {
    // Option-level next: for multiselect, selectedValue is an array — use first option that has next
    if (Array.isArray(selectedValue)) {
      for (const v of selectedValue) {
        const o = (currentQ.options || []).find((op) => op.value === v);
        if (o?.next) return o.next;
      }
    } else {
      const opt = (currentQ.options || []).find((o) => o.value === selectedValue);
      if (opt?.next) return opt.next;
    }

    // Question-level next
    if (currentQ.next) return currentQ.next;

    // Default: next visible in array order
    const currentIndex = getQuestionIndexById(currentQ.id);
    for (let i = currentIndex + 1; i < schema.questions.length; i++) {
      if (isQuestionVisible(schema.questions[i])) return schema.questions[i].id;
    }
    return null;
  }

  function purgeHiddenAnswers() {
    // Remove answers for questions that are no longer visible
    for (const q of schema.questions) {
      if (!isQuestionVisible(q) && typeof answers[q.id] !== "undefined") {
        delete answers[q.id];
      }
    }
  }

  function inferResultsPageId() {
    return "results_full";
  }

  // ----------------------------
  // Result logic: tally Q1–Q5 by option order (A/B/C/D/E), most points wins
  // Option order in schema = A, B, C, D, E → Adventurous Romantics, Cozy Intimates, Curious Explorers, Social Sparks, Playful Competitors
  // ----------------------------
  const TALLY_QUESTION_IDS = ["q1_excited_about", "q2_best_date_feeling", "q3_restaurant_most_about", "q4_relationship_energy", "q5_surprise_prefer"];
  const LETTER_TO_PROFILE = {
    A: "adventurous_romantics",
    B: "cozy_intimates",
    C: "curious_explorers",
    D: "social_sparks",
    E: "playful_competitors",
  };
  const LETTER_TO_PACK = {
    A: "QP_ADV_01",
    B: "QP_COZY_01",
    C: "QP_CUR_01",
    D: "QP_SOC_01",
    E: "QP_PLAY_01",
  };
  const TALLY_ORDER = ["A", "B", "C", "D", "E"];

  function computeResult() {
    const tally = { A: 0, B: 0, C: 0, D: 0, E: 0 };
    for (const qid of TALLY_QUESTION_IDS) {
      const q = schema.questions.find((x) => x.id === qid);
      if (!q || !Array.isArray(q.options)) continue;
      const value = answers[qid];
      if (value == null || value === "") continue;
      const idx = q.options.findIndex((o) => String(o.value) === String(value));
      if (idx >= 0 && idx <= 4) {
        const letter = TALLY_ORDER[idx];
        tally[letter] = (tally[letter] || 0) + 1;
      }
    }
    let bestLetter = "A";
    let bestCount = 0;
    for (const letter of TALLY_ORDER) {
      const count = tally[letter] || 0;
      if (count > bestCount) {
        bestCount = count;
        bestLetter = letter;
      }
    }
    return {
      profile: LETTER_TO_PROFILE[bestLetter] || "adventurous_romantics",
      question_pack_id: LETTER_TO_PACK[bestLetter] || "QP_ADV_01",
    };
  }

  // ----------------------------
  // Persistence
  // ----------------------------
  function persistResult(result) {
    localStorage.setItem(LS.answers, JSON.stringify(answers));
    localStorage.setItem(LS.profile, result.profile || "");
    localStorage.setItem(LS.pack, result.question_pack_id || "");
    localStorage.setItem(LS.schemaId, schema.id || "");
    localStorage.setItem(LS.history, JSON.stringify(historyStack));
    setBool(LS.completed, true);
  }

  function saveProgress() {
    localStorage.setItem(LS.answers, JSON.stringify(answers));
    localStorage.setItem(LS.schemaId, schema.id || "");
    localStorage.setItem(LS.history, JSON.stringify(historyStack));
  }

  function clearSavedProgressOnly() {
    localStorage.removeItem(LS.answers);
    localStorage.removeItem(LS.history);
    localStorage.removeItem(LS.schemaId);
    // don't clear LS.seen here; keep "seen" behavior stable
    localStorage.removeItem(LS.completed);
    localStorage.removeItem(LS.profile);
    localStorage.removeItem(LS.pack);
  }

  function loadSavedAnswers() {
    try {
      return JSON.parse(localStorage.getItem(LS.answers) || "null");
    } catch {
      return null;
    }
  }

  function loadSavedHistory() {
    try {
      return JSON.parse(localStorage.getItem(LS.history) || "null");
    } catch {
      return null;
    }
  }

  // ----------------------------
  // Cart attachment (line item properties)
  // ----------------------------
  function hasQuizSaved() {
    return getBool(LS.completed) && !!localStorage.getItem(LS.profile) && !!localStorage.getItem(LS.pack);
  }

  /** Turn answers object + schema into "Q1: ANSWER" style lines for cart display (no question text, just number + answer). */
  function getQuizSummaryForCart(questions, answersObj) {
    if (!questions || !Array.isArray(questions) || !answersObj || typeof answersObj !== "object") return "";
    const lines = [];
    questions.forEach((q, i) => {
      const raw = answersObj[q.id];
      if (raw === undefined || raw === null || raw === "") return;
      let display = "";
      if (q.type === "multiselect") {
        const arr = Array.isArray(raw)
          ? raw
          : typeof raw === "string" && raw.trim().startsWith("[")
          ? (() => {
              try {
                return JSON.parse(raw);
              } catch {
                return [];
              }
            })()
          : [raw];
        const labels = (q.options || []).filter((o) => arr.includes(o.value)).map((o) => o.label);
        display = labels.length ? labels.join(", ") : String(raw);
      } else if (q.type === "radio" || q.type === "dropdown") {
        const opt = (q.options || []).find((o) => o.value === raw);
        display = opt ? opt.label : String(raw);
      } else {
        display = String(raw).trim();
      }
      if (display) lines.push("Q" + (i + 1) + ": " + display);
    });
    return lines.join("\n");
  }

  /** Build object mapping question title -> human-readable answer (for backend storage). */
  function getQuestionAnswerObject(questions, answersObj) {
    if (!questions || !Array.isArray(questions) || !answersObj || typeof answersObj !== "object") return {};
    const out = {};
    questions.forEach((q) => {
      const raw = answersObj[q.id];
      if (raw === undefined || raw === null || raw === "") return;
      const title = (q.title || q.id || "").trim();
      if (!title) return;
      let display = "";
      if (q.type === "multiselect") {
        const arr = Array.isArray(raw)
          ? raw
          : typeof raw === "string" && raw.trim().startsWith("[")
          ? (() => {
              try {
                return JSON.parse(raw);
              } catch {
                return [];
              }
            })()
          : [raw];
        const labels = (q.options || []).filter((o) => arr.includes(o.value)).map((o) => o.label);
        display = labels.length ? labels.join(", ") : String(raw);
      } else if (q.type === "radio" || q.type === "dropdown") {
        const opt = (q.options || []).find((o) => o.value === raw);
        display = opt ? opt.label : String(raw);
      } else {
        display = String(raw).trim();
      }
      if (display) out[title] = display;
    });
    return out;
  }

  /** Extract email and name from answers for customer creation (e.g. backend/Shopify Flow can use when order is placed). */
  function getCustomerPayload(answersObj) {
    if (!answersObj || typeof answersObj !== "object") return null;
    const email = (answersObj.email_full || answersObj.email_optin_only || "").trim();
    const name = (answersObj.name || "").trim() || undefined;
    if (!email) return null;
    return { email, name };
  }

  /** If data-customer-create-url is set, POST customer + full quiz data so the backend can create a customer and save answers. */
  function tryCreateCustomerFromAnswers() {
    const url = (root.dataset.customerCreateUrl || "").trim();
    if (!url) return;
    const payload = getCustomerPayload(answers);
    if (!payload) return;
    const summary = getQuizSummaryForCart(schema?.questions || [], answers);
    const question_answer = getQuestionAnswerObject(schema?.questions || [], answers);
    const emailConsent = !!(answers.email_full_consent || answers.email_optin_only_consent);
    const result = computeResult();
    const body = {
      email: payload.email,
      name: payload.name,
      schema_id: schema?.id || null,
      answers: answers,
      summary: summary || null,
      question_answer: question_answer,
      email_consent: emailConsent,
      profile: result?.profile || null,
      profile_label: getProfileStyleLabel(result?.profile) || null,
      city_label: getCityLabelFromAnswers(answers) || null,
      constraints: getConstraintsFromAnswers(answers) || null,
      budget_label: getBudgetLabelFromAnswers(answers) || null,
      partner_name: (answers.partner_name && String(answers.partner_name).trim()) || null,
    };
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body),
    })
      .then(function (res) {
        return res.json().catch(function () {
          return null;
        });
      })
      .then(function (data) {
        if (data && data.submission_id != null) {
          localStorage.setItem(LS.submissionId, String(data.submission_id));
          maybeAutoAttachLoop();
        }
      })
      .catch(function (err) {
        console.warn("[LVQ] Customer create request failed:", err);
      });
  }

  function buildProperties() {
    const profile = localStorage.getItem(LS.profile) || "";
    const pack = localStorage.getItem(LS.pack) || "";
    const ans = localStorage.getItem(LS.answers) || "{}";
    const schemaId = localStorage.getItem(LS.schemaId) || "";
    const cartMode = (root.dataset.cartAnswers || "summary").toLowerCase();

    const base = {
      [`${propertyPrefix} Profile`]: profile,
      [`${propertyPrefix} Question Pack`]: pack,
      [`${propertyPrefix} Schema ID`]: schemaId,
    };

    if (cartMode === "hidden") return base;

    let answersObj = {};
    try {
      answersObj = JSON.parse(ans) || {};
    } catch {}

    if (cartMode === "id") {
      const submissionId = (localStorage.getItem(LS.submissionId) || "").trim();
      const cityLabel = getCityLabelFromAnswers(answersObj);
      const constraintsText = getConstraintsFromAnswers(answersObj);
      return {
        ...(submissionId ? { [`${propertyPrefix} Quiz ID`]: submissionId } : {}),
        ...(cityLabel ? { [`${propertyPrefix} City`]: cityLabel } : {}),
        ...(constraintsText ? { [`${propertyPrefix} Constraints`]: constraintsText } : {}),
      };
    }

    if (cartMode === "summary") {
      base[`${propertyPrefix} Quiz`] = getQuizSummaryForCart(schema?.questions || [], answersObj);
      const customer = getCustomerPayload(answersObj);
      if (customer) {
        base[`${propertyPrefix} Email`] = customer.email;
        if (customer.name) base[`${propertyPrefix} Name`] = customer.name;
      }
      return base;
    }

    // full (default fallback)
    base[`${propertyPrefix} Quiz Answers`] = ans;
    const customer = getCustomerPayload(answersObj);
    if (customer) {
      base[`${propertyPrefix} Email`] = customer.email;
      if (customer.name) base[`${propertyPrefix} Name`] = customer.name;
    }
    return base;
  }

  async function getCart() {
    const res = await fetch("/cart.js", { headers: { Accept: "application/json" } });
    if (!res.ok) throw new Error("Failed to load cart");
    return res.json();
  }

  function findTargetLine(cart, handleOverride) {
    const handle = handleOverride != null && handleOverride !== "" ? handleOverride : productHandle;
    if (!handle) return null;

    const idx = cart.items.findIndex((i) => i.handle === handle);
    if (idx === -1) return null;
    return { line: idx + 1, item: cart.items[idx] };
  }

  function itemAlreadyHasProperties(item) {
    if (!item?.properties) return false;
    const cartMode = (root.dataset.cartAnswers || "summary").toLowerCase();
    if (cartMode === "id") return !!item.properties[`${propertyPrefix} Quiz ID`];
    return !!item.properties[`${propertyPrefix} Question Pack`];
  }

  async function attachToCartIfPresent() {
    if (!hasQuizSaved()) return false;

    const savedProfile = localStorage.getItem(LS.profile) || "";
    const handleToFind = schema?.results?.product_by_profile && savedProfile ? getProductHandleForProfile(savedProfile) : productHandle;

    const cart = await getCart();
    const target = findTargetLine(cart, handleToFind);
    if (!target) return false;

    if (itemAlreadyHasProperties(target.item)) return true;

    const properties = buildProperties();
    if (Object.keys(properties).length === 0) return false;

    const res = await fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ line: target.line, properties }),
    });
    if (!res.ok) throw new Error("Failed to update cart item properties");

    return true;
  }

  // Poll loop to apply properties if/when they add the product later
  function maybeAutoAttachLoop() {
    if (autoAttachTimer) return;

    function runAttach() {
      attachToCartIfPresent()
        .then(function (applied) {
          if (applied) {
            if (autoAttachTimer) {
              clearInterval(autoAttachTimer);
              autoAttachTimer = null;
            }
          }
        })
        .catch(function () {});
    }

    runAttach();
    autoAttachTimer = setInterval(runAttach, 800);
  }

  // ----------------------------
  // Hook into theme's add-to-cart/cart updates
  // ----------------------------
  function hookAjaxCart() {
    const origFetch = window.fetch;
    if (origFetch.__lvq_wrapped) return;

    function isQuizVariant(variantId) {
      if (!variantId) return false;
      const id = typeof variantId === "number" ? variantId : parseInt(variantId, 10);
      return quizVariantIds.has(id) || quizVariantIds.has(variantId);
    }

    function injectPropertiesIntoAddBody(body) {
      if (!hasQuizSaved()) return body;
      const props = buildProperties();
      if (Object.keys(props).length === 0) return body;

      try {
        let payload = typeof body === "string" ? JSON.parse(body) : body;
        if (!payload || typeof payload !== "object") return body;

        if (Array.isArray(payload.items)) {
          payload.items = payload.items.map(function (item) {
            const vid = item?.id ?? item?.variant_id;
            if (isQuizVariant(vid)) {
              item = { ...item };
              item.properties = { ...(item.properties || {}), ...props };
            }
            return item;
          });
          return JSON.stringify(payload);
        }

        const vid = payload.id ?? payload.variant_id;
        if (isQuizVariant(vid)) {
          payload = { ...payload };
          payload.properties = { ...(payload.properties || {}), ...props };
          return JSON.stringify(payload);
        }
      } catch (e) {}
      return body;
    }

    function wrappedFetch(input, init) {
      const url = typeof input === "string" ? input : input?.url || "";
      const isAdd = url.indexOf("/cart/add") !== -1;
      const method = (init && typeof init.method === "string" ? init.method : "GET").toUpperCase();

      if (isAdd && method === "POST" && init && init.body) {
        init = { ...init, body: injectPropertiesIntoAddBody(init.body) };
      }

      return origFetch.call(this, input, init).then(function (res) {
        try {
          if (url && (url.indexOf("/cart/add") !== -1 || url.indexOf("/cart/change") !== -1 || url.indexOf("/cart/update") !== -1)) {
            attachToCartIfPresent().catch(function () {});
          }
        } catch (e) {}
        return res;
      });
    }

    wrappedFetch.__lvq_wrapped = true;
    window.fetch = wrappedFetch;
  }

  // ----------------------------
  // UI helpers / styles
  // ----------------------------
  function showError(msg) {
    const el = document.getElementById("lvqError");
    if (!el) return;
    el.textContent = msg;
    el.style.display = "block";
  }

  function hideError() {
    const el = document.getElementById("lvqError");
    if (!el) return;
    el.textContent = "";
    el.style.display = "none";
  }

  function injectStylesOnce() {
    if (document.getElementById("lvqModalStyles")) return;
    const style = document.createElement("style");
    style.id = "lvqModalStyles";
    style.textContent = `
        :root{
          --lvq-bg: #F6E9E3;
          --lvq-surface: #FFFFFF;
          --lvq-border: rgba(92, 35, 35, .18);
          --lvq-text: #4A1E1E;
          --lvq-muted: rgba(74, 30, 30, .70);
  
          --lvq-primary: #6B1F1F;
          --lvq-primaryText: #FFFFFF;
  
          --lvq-hover: rgba(107, 31, 31, .08);
          --lvq-shadow: 0 18px 50px rgba(0,0,0,.10);
          --lvq-radius: 18px;
  
          --lvq-font: var(--font-body-family, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial);
          --lvq-serif: var(--font-heading-family, ui-serif, Georgia, "Times New Roman", Times, serif);
        }
  
        body.lvq-lock { overflow: hidden; }

        .lvq-load-error {
          padding: 24px 20px;
          text-align: center;
          color: var(--lvq-muted);
          font-size: 15px;
          max-width: 360px;
          margin: 0 auto;
        }
  
        .lvq-fullscreen {
          position: fixed;
          inset: 0;
          z-index: 999999;
          background: var(--lvq-bg);
          color: var(--lvq-text);
          font-family: var(--lvq-font);
          display: flex;
          flex-direction: column;
        }
  
        .lvq-topbar {
          height: 64px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 18px;
          border-bottom: 1px solid var(--lvq-border);
          background: rgba(255,255,255,.35);
          backdrop-filter: blur(8px);
        }
  
        .lvq-brand {
          display: flex;
          align-items: center;
          gap: 10px;
          font-weight: 700;
          letter-spacing: .2px;
          font-family: var(--lvq-serif);
        }
  
        .lvq-navbar-logo img, .lvq-navbar-logo svg, .lvq-navbar-logo .lvq-fallback-logo {
          height: 26px;
          min-height: 26px;
          width: auto;
          min-width: 1px;
          display: block;
          object-fit: contain;
        }
        .lvq-navbar-logo { min-height: 26px; display: flex; align-items: center; }
  
        .lvq-brandtext { font-size: 20px; }
  
        .lvq-close {
          border: 1px solid var(--lvq-border);
          background: #fff;
          color: var(--lvq-text);
          border-radius: 999px;
          padding: 10px 12px;
          cursor: pointer;
          font-weight: 650;
        }
  
        /* Centered column */
        .lvq-shell {
          flex: 1;
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 34px 16px 40px;
        }
  
        .lvq-column {
          width: min(560px, 100%);
          display: flex;
          flex-direction: column;
          align-items: center;
        }
  
        .lvq-header {
          width: 100%;
          text-align: center;
          margin-bottom: 14px;
        }
  
        .lvq-header-logo {
          display: flex;
          justify-content: center;
          margin-bottom: 6px;
        }
        .lvq-header-logo img, .lvq-header-logo svg, .lvq-header-logo .lvq-fallback-logo {
          height: 32px;
          min-height: 32px;
          width: auto;
          min-width: 1px;
          display: block;
          object-fit: contain;
        }
        .lvq-header-logo .lvq-navbar-logo { min-height: 32px; display: flex; align-items: center; }
        .lvq-header-logo .lvq-brandtext {
          font-family: var(--lvq-serif);
          font-size: 26px;
          font-weight: 700;
        }
  
        .lvq-sub {
          margin: 0;
          color: var(--lvq-muted);
          font-size: 15px;
        }
  
        .lvq-card {
          width: 100%;
          background: var(--lvq-surface);
          border: 1px solid var(--lvq-border);
          border-radius: var(--lvq-radius);
          box-shadow: var(--lvq-shadow);
          overflow: hidden;
        }
  
        .lvq-card__inner { padding: 18px; }
        .lvq-card__scroll { flex: 1; min-height: 0; }
  
        /* Progress */
        .lvq-progress-wrap { margin: 0 0 18px; }
        .lvq-progress {
          height: 6px;
          background: rgba(107,31,31,.12);
          border-radius: 999px;
          overflow: hidden;
          margin: 0 0 8px;
        }
        .lvq-progress__bar {
          height: 100%;
          background: var(--lvq-primary);
          border-radius: 999px;
          transition: width .35s ease-out;
        }
        .lvq-progress-copy {
          margin: 0;
          font-size: 13px;
          color: var(--lvq-muted);
          font-weight: 600;
        }
        @media (prefers-reduced-motion: reduce) {
          .lvq-progress__bar { transition: none; }
          .lvq-option:hover { transform: none; }
          .lvq-btn--primary:hover { filter: none; }
          .lvq-results-pre-reveal,
          .lvq-results-revealed { transition: none; }
          .lvq-btn--reveal.lvq-reveal--loading { animation: none; }
        }
  
        .lvq-h { margin: 0 0 10px; font-family: var(--lvq-serif); font-size: 22px; }
        .lvq-h2 { font-family: var(--lvq-serif); font-size: 20px; margin: 0 0 14px; }
        .lvq-p { margin: 0 0 14px; color: var(--lvq-muted); }
        .lvq-hint { font-size: 12px; color: var(--lvq-muted); margin-top: 8px; }
  
        /* Options wrappers — mobile scroll only */
        .lvq-options-wrap,
        .lvq-chips-wrap {
          position: relative;
        }

        /* Radio options — default stacked */
        .lvq-options { display: grid; gap: 12px; }
        .lvq-options--chips {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }
        .lvq-option {
          width: 100%;
          text-align: left;
          padding: 16px 16px;
          min-height: 48px;
          border-radius: 14px;
          border: 1px solid var(--lvq-border);
          background: #fff;
          color: var(--lvq-text);
          cursor: pointer;
          font-size: 15px;
          font-weight: 650;
          transition: background .15s ease, border-color .15s ease, transform .08s ease;
        }
        .lvq-option--chip {
          width: auto;
          min-width: 0;
          min-height: 44px;
          padding: 10px 14px;
          border-radius: 999px;
          font-size: 14px;
        }
        .lvq-option:hover {
          background: rgba(107,31,31,.06) !important;
          border-color: rgba(107,31,31,.35);
          transform: translateY(-1px);
          color: #000 !important;
        }
        .lvq-option--chip:hover { transform: none; }
        .lvq-option.is-selected {
          background: rgba(107,31,31,.10) !important;
          border-color: rgba(107,31,31,.70);
          box-shadow: 0 10px 22px rgba(107,31,31,.12);
        }
        .lvq-option:active,
        .lvq-option:focus,
        .lvq-option:focus-visible,
        .lvq-chip:active,
        .lvq-chip:focus,
        .lvq-chip:focus-visible {
          outline: none;
          color: var(--lvq-text);
          -webkit-tap-highlight-color: transparent;
        }
        .lvq-option.is-selected:active,
        .lvq-option.is-selected:focus,
        .lvq-chip.is-selected:active,
        .lvq-chip.is-selected:focus {
          color: var(--lvq-text);
        }

        /* Inputs */
        .lvq-select, .lvq-input, .lvq-textarea {
          width: 100%;
          border: 1px solid var(--lvq-border);
          border-radius: 14px;
          padding: 13px 12px;
          font-size: 16px;
          background: #fff;
          color: var(--lvq-text);
          outline: none;
        }
        .lvq-select option {
          color: var(--lvq-text);
          background: #fff;
        }
        .lvq-textarea { min-height: 120px; resize: vertical; }

        .lvq-email-consent { margin-top: 12px; }
        .lvq-consent-label {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          cursor: pointer;
          font-size: 13px;
          font-weight: 400;
          color: var(--lvq-muted);
          line-height: 1.4;
        }
        .lvq-consent-checkbox {
          flex-shrink: 0;
          margin-top: 3px;
          width: 18px;
          height: 18px;
          accent-color: var(--lvq-primary);
        }
        .lvq-consent-text { flex: 1; }

        /* Multi-select chips */
        .lvq-chips {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }
  
        .lvq-chip {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 10px 12px;
          min-height: 44px;
          border-radius: 999px;
          border: 1px solid var(--lvq-border);
          background: #fff;
          color: var(--lvq-text);
          cursor: pointer;
          font-size: 14px;
          font-weight: 650;
          line-height: 1.1;
          max-width: 100%;
          transition: background .15s ease, border-color .15s ease;
        }
  
        .lvq-chip:hover {
          background: rgba(107,31,31,.05);
          border-color: rgba(107,31,31,.35);
          transform: none;
          box-shadow: none;
        }
  
        .lvq-chip.is-selected {
          background: rgba(107,31,31,.10);
          border-color: rgba(107,31,31,.65);
        }
  
        /* Buttons — min 44px touch target */
        .lvq-actions { display: flex; gap: 10px; margin-top: 16px; }
        .lvq-btn {
          padding: 13px 14px;
          min-height: 48px;
          border-radius: 999px;
          border: 1px solid var(--lvq-border);
          background: #fff;
          color: var(--lvq-text);
          cursor: pointer;
          width: 100%;
          font-size: 15px;
          font-weight: 750;
          text-align: center;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .lvq-btn--primary {
          background: var(--lvq-primary);
          border-color: var(--lvq-primary);
          color: var(--lvq-primaryText);
          transition: background .2s ease, filter .2s ease;
        }
        .lvq-btn--primary:hover {
          background: #5a1a1a;
          filter: brightness(1.02);
        }
        .lvq-btn--ghost { background: #fff; }
        .lvq-btn[disabled] { opacity: .55; cursor: not-allowed; }
  
        .lvq-error { margin-top: 12px; color: #8B1E1E; font-size: 13px; font-weight: 650; }
        .lvq-foot {
          margin-top: 20px;
          font-size: 12px;
          color: var(--lvq-muted);
          text-align: center;
          line-height: 1.4;
        }
  
        /* Results success — obvious, minimal reading */
        .lvq-results-success {
          text-align: center;
          padding: 16px 0 24px;
        }
        .lvq-results-pre-reveal {
          text-align: center;
          padding: 28px 0 32px;
        }
        .lvq-results-pre-reveal .lvq-results-success__icon {
          margin: 0 auto 24px;
          line-height: 0;
        }
        .lvq-results-pre-reveal .lvq-results-success__icon svg {
          display: block;
          margin: 0 auto;
        }
        .lvq-results-style-line {
          margin: 0 0 28px;
          font-size: 18px;
          font-family: var(--lvq-font);
          color: var(--lvq-text);
          line-height: 1.45;
          max-width: 320px;
          margin-left: auto;
          margin-right: auto;
        }
        .lvq-results-style-line strong {
          color: var(--lvq-primary);
          font-weight: 700;
        }
        .lvq-btn--reveal {
          width: 100%;
          min-width: 0;
          padding: 14px 20px;
          min-height: 52px;
          font-size: 16px;
          letter-spacing: 0.02em;
        }
        .lvq-results-revealed {
          padding: 8px 0 0;
          opacity: 0;
          transform: translateY(14px);
          transition: opacity 0.42s ease-out, transform 0.42s ease-out;
        }
        .lvq-results-revealed.lvq-revealed--ready {
          opacity: 1;
          transform: translateY(0);
        }
        .lvq-results-pre-reveal {
          transition: opacity 0.35s ease-out, transform 0.35s ease-out;
        }
        .lvq-results-pre-reveal.lvq-pre-reveal--out {
          opacity: 0;
          transform: scale(0.97);
          pointer-events: none;
        }
        .lvq-btn--reveal.lvq-reveal--loading {
          animation: lvq-reveal-pulse 0.9s ease-in-out infinite;
        }
        @keyframes lvq-reveal-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.78; }
        }
        .lvq-results-revealed .lvq-results-success {
          padding: 0 0 20px;
          text-align: center;
        }
        .lvq-results-revealed .lvq-results-success__icon { display: none; }
        .lvq-results-success__icon {
          margin: 0 auto 20px;
          line-height: 0;
        }
        .lvq-results-success__icon svg {
          display: block;
          margin: 0 auto;
        }
        .lvq-results-headline {
          font-family: var(--lvq-serif);
          font-size: clamp(22px, 4vw, 28px);
          margin: 0 0 12px;
          color: var(--lvq-text);
          line-height: 1.25;
          font-weight: 600;
        }
        .lvq-results-headline__intro {
          display: block;
          font-family: var(--lvq-font);
          font-size: 0.65em;
          font-weight: 500;
          color: var(--lvq-muted);
          margin-bottom: 0.2em;
        }
        .lvq-results-headline__style {
          display: block;
          margin-top: 0.25em;
          font-family: var(--lvq-serif);
          font-weight: 800;
          font-size: 1.2em;
          color: var(--lvq-primary);
        }
        .lvq-results-sub {
          margin: 0;
          font-size: 15px;
          font-family: var(--lvq-font);
          color: var(--lvq-muted);
          line-height: 1.5;
          max-width: 300px;
          margin-left: auto;
          margin-right: auto;
        }
        .lvq-actions--stacked {
          margin-top: 8px;
        }
        .lvq-actions--stacked .lvq-btn {
          width: 100%;
          min-height: 52px;
          padding: 14px 20px;
          font-size: 16px;
        }
        .lvq-results-secondary {
          text-align: left;
          margin-top: 16px;
        }
        .lvq-link {
          background: none;
          border: none;
          padding: 0;
          font-size: 13px;
          color: var(--lvq-muted);
          cursor: pointer;
          text-decoration: none;
          transition: color .2s ease;
        }
        .lvq-link:hover { color: var(--lvq-text); }
        .lvq-resultbox {
          border: 1px solid var(--lvq-border);
          border-radius: 14px;
          padding: 14px 18px;
          background: rgba(107,31,31,.06);
          margin: 0 0 20px;
          color: var(--lvq-text);
          display: grid;
          gap: 10px;
        }
        .lvq-resultbox__row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
        }
        .lvq-resultbox__label {
          font-size: 14px;
          color: var(--lvq-muted);
        }
        .lvq-resultbox__value {
          font-weight: 700;
          font-size: 15px;
        }
  
        /* Mobile — full page, cream background, logo visible, buttons pinned left/right */
        @media (max-width: 600px) {
          .lvq-fullscreen {
            min-height: 100vh; min-height: 100dvh;
            background: var(--lvq-bg);
          }
          .lvq-shell { padding: 0; justify-content: flex-start; align-items: stretch; overflow: hidden; flex: 1; min-height: 0; }
          .lvq-column { width: 100%; max-width: 100%; min-height: 0; margin: 0; flex: 1; display: flex; flex-direction: column; padding: 0 12px; box-sizing: border-box; }
          .lvq-header { margin-bottom: 8px; flex-shrink: 0; padding-top: 8px; }
          .lvq-header-logo img, .lvq-header-logo svg, .lvq-header-logo .lvq-fallback-logo {
            height: 28px !important; min-height: 28px !important; width: auto !important; min-width: 80px !important; max-width: 160px; object-fit: contain; object-position: center;
          }
          .lvq-header-logo .lvq-navbar-logo { min-height: 28px; min-width: 80px; }
          .lvq-card { border-left: none; border-right: none; border-radius: 14px 14px 0 0; }
          .lvq-sub { font-size: 14px; }
          .lvq-card { min-height: 0; flex: 1; display: flex; flex-direction: column; overflow: hidden; }
          .lvq-card__inner { padding: 0; overflow-y: auto; overflow-x: hidden; flex: 1; min-height: 0; display: flex; flex-direction: column; -webkit-overflow-scrolling: touch; }
          .lvq-card__scroll {
            flex: 1; min-height: 0; overflow-y: auto; -webkit-overflow-scrolling: touch;
            padding: 16px 16px 20px;
          }
          .lvq-progress-wrap { margin-bottom: 14px; flex-shrink: 0; }
          .lvq-h2 { font-size: 18px; margin-bottom: 12px; }
          .lvq-options-wrap .lvq-options,
          .lvq-chips-wrap .lvq-chips {
            max-height: none;
            overflow-y: visible;
          }
          .lvq-options { gap: 10px; }
          .lvq-option { padding: 14px 14px; min-height: 48px; font-size: 15px; text-align: center; display: flex; align-items: center; justify-content: center; }
          .lvq-option--chip { padding: 12px 16px; min-height: 46px; text-align: center; display: inline-flex; align-items: center; justify-content: center; }
          .lvq-actions {
            margin-top: 0; gap: 12px; flex-direction: row; flex-shrink: 0;
            justify-content: space-between;
            padding: 16px 16px calc(16px + env(safe-area-inset-bottom));
            background: var(--lvq-surface);
            border-top: 1px solid var(--lvq-border);
          }
          .lvq-actions .lvq-btn {
            flex: 1; min-width: 0; width: auto;
            min-height: 50px; font-size: 16px;
            text-align: center !important;
            justify-content: center !important;
            display: inline-flex;
            align-items: center;
          }
          .lvq-chips { gap: 8px; }
          .lvq-chip { min-height: 46px; padding: 12px 14px; text-align: center; display: inline-flex; align-items: center; justify-content: center; }
          .lvq-results-success { padding: 24px 20px 20px; }
          .lvq-results-pre-reveal {
            padding: 32px 20px 28px;
          }
          .lvq-results-pre-reveal .lvq-results-success__icon { margin-bottom: 20px; }
          .lvq-results-style-line {
            font-size: 17px;
            margin-bottom: 24px;
            padding: 0 4px;
          }
          .lvq-results-success__icon { margin-bottom: 16px; }
          .lvq-results-headline { font-size: 22px; margin-bottom: 10px; }
          .lvq-results-sub { font-size: 14px; max-width: 100%; line-height: 1.5; }
          .lvq-results-revealed { padding: 16px 20px 0; }
          .lvq-actions--stacked {
            margin-top: 0;
            padding: 20px 20px calc(20px + env(safe-area-inset-bottom));
            border-top: 1px solid var(--lvq-border);
            background: var(--lvq-surface);
            flex-shrink: 0;
          }
          .lvq-actions--stacked .lvq-btn { min-height: 52px; font-size: 16px; width: 100%; }
          .lvq-results-secondary { margin-top: 14px; padding: 0; text-align: left; }
          .lvq-foot { font-size: 12px; margin-top: 18px; padding: 0; line-height: 1.4; }
        }
        @media (max-width: 380px) {
          .lvq-topbar { height: 56px; padding: 0 12px; }
          .lvq-card__inner { padding: 16px 14px; }
          .lvq-option--chip { padding: 10px 12px; font-size: 13px; }
        }
      `;
    document.head.appendChild(style);
  }

  // ----------------------------
  // Utils
  // ----------------------------
  function getBool(key) {
    return localStorage.getItem(key) === "1";
  }
  function setBool(key, v) {
    localStorage.setItem(key, v ? "1" : "0");
  }

  function isEmpty(v) {
    if (Array.isArray(v)) return v.length === 0;
    return v == null || String(v).trim() === "";
  }

  function escapeHtml(str) {
    return String(str ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");
  }
  function escapeAttr(str) {
    return escapeHtml(str).replaceAll("`", "&#096;");
  }
  function cssEscape(str) {
    return window.CSS && CSS.escape ? CSS.escape(str) : String(str).replace(/"/g, '\\"');
  }

  // --- Public API: open quiz on demand from anywhere ---
  window.LVQ = window.LVQ || {};
  window.LVQ.open = function openLoventuresQuiz(opts) {
    const { startAt = "firstQuestion" } = opts || {};
    if (!document.getElementById("lvQuizModalRoot")) return;
    if (document.querySelector(".lvq-fullscreen")) return;
    if (!schema || !Array.isArray(schema.questions) || schema.questions.length === 0) {
      console.warn("[LVQ] Schema not loaded yet. Quiz will open when ready.");
      return;
    }

    openModal();
    setHeaderText();
    answers = loadSavedAnswers() || {};
    historyStack = loadSavedHistory() || [];

    if (startAt === "firstQuestion") {
      currentQuestionId = getFirstVisibleQuestionId();
      if (!currentQuestionId) {
        renderIntro();
      } else {
        visitedQuestionIds = new Set();
        schema.questions.forEach((q) => {
          if (answers[q.id] !== undefined) visitedQuestionIds.add(q.id);
        });
        saveProgress();
        renderQuestion();
      }
    } else {
      renderIntro();
    }
    focusFirstInContent();
  };
})();
